import { ArrowLeftIcon, SaveIcon, SparklesIcon } from 'lucide-react';

import { useEffect, useRef, useState } from 'react';

import {
  type DescribeUseRowContext,
  EXAMPLE_GROUP_ID,
  SYNC_LINE_ITEMS_NOT_ALLOWED_ERROR,
  type ScanFieldKey,
  type ScanMerge,
  currentUtcMonth,
  describeUseRow,
  formatCurrency,
  formatDate,
  isPdfReceiptUrl,
  offlineExpenseService,
  scanAvailability,
  switchConfirmKey,
  unpickedUnits,
} from '@splitfiesta/core';
import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import {
  GROUP_EXPENSE_LIMIT_ERROR_KEYS,
  type GroupProDto,
} from '@splitfiesta/shared';
import { Alert, Button, FormButton } from '@splitfiesta/ui';

import { withAuth } from '@/components/auth';
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog';
import { ImageViewerModal } from '@/components/common/ImageViewerModal';
import { RouteChangeLink } from '@/components/common/RouteChangeLink';
import { FormErrorBoundary } from '@/components/errors';
// Import all form components statically (bundled together for better performance)
import { ExpenseAdditionalDetails } from '@/components/expenses/ExpenseAdditionalDetails';
import { ExpenseBasicInfo } from '@/components/expenses/ExpenseBasicInfo';
import { ExpenseDate } from '@/components/expenses/ExpenseDate';
import { ExpenseLimitNotice } from '@/components/expenses/ExpenseLimitNotice';
import { ExpensePaymentDetails } from '@/components/expenses/ExpensePaymentDetails';
import { ExpenseReceiptModal } from '@/components/expenses/ExpenseReceiptModal';
import { ExpenseReceiptOfflineNotice } from '@/components/expenses/ExpenseReceiptOfflineNotice';
import { ExpenseReceiptUpload } from '@/components/expenses/ExpenseReceiptUpload';
import { ExpenseRepeat } from '@/components/expenses/ExpenseRepeat';
import { ProgressiveDisclosureToggle } from '@/components/expenses/ProgressiveDisclosureToggle';
import { SaveSpotField } from '@/components/expenses/SaveSpotField';
import { SplitRecap } from '@/components/expenses/SplitRecap';
import { UnifiedExpenseSplit } from '@/components/expenses/UnifiedExpenseSplit';
import { SplitByItemCard } from '@/components/expenses/lineItems/SplitByItemCard';
import { SplitByItemDialog } from '@/components/expenses/lineItems/SplitByItemDialog';
import { ReceiptBlock } from '@/components/expenses/receipt/ReceiptBlock';
import { AppLayout } from '@/components/layout/AppLayout';
import { ProGroupDialogContainer } from '@/components/pro/ProGroupDialogContainer';
import {
  ExpenseCreateSkeleton,
  ExpenseEditSkeleton,
} from '@/components/skeleton';
import { BackButton } from '@/components/ui/BackButton';

import { useFeatureFlag } from '@/hooks/queries/useFeatureFlagsQuery';
import { useDelayedFlag } from '@/hooks/useDelayedFlag';
import { type SavedSpotRole, useExpenseForm } from '@/hooks/useExpenseForm';
import { useReceiptScan } from '@/hooks/useReceiptScan';
import { useSaveSpotFromForm } from '@/hooks/useSaveSpotFromForm';

import { ExpenseBasicInfoService } from '@/services/expense/ExpenseBasicInfoService';

import { useOfflineStore } from '@/stores/offlineStore';
import { useScanSessionFlags } from '@/stores/scanSessionStore';

interface ExpenseFormProps {
  groupId: string;
  expenseId?: string;
  mode?: 'create' | 'edit';
}

type ExpenseFormState = ReturnType<typeof useExpenseForm>;
type UseRow = ScanMerge['useRows'][number];

function ExpenseForm({
  groupId,
  expenseId,
  mode = 'create',
}: ExpenseFormProps) {
  const { t, ready } = useTranslation('expenses');
  const proFlagOn = useFeatureFlag('pro');
  const form = useExpenseForm({ groupId, expenseId, mode, proFlagOn });
  const { user, group, error, initialLoading, expenseLimit } = form;
  const [proDialogOpen, setProDialogOpen] = useState(false);

  // Load required namespaces
  useNamespace('expenses');
  useNamespace('groups');
  useNamespace('currencies');

  if (!user) {
    return null;
  }

  // Show skeleton immediately while translations or data are loading
  if (!ready || initialLoading) {
    const SkeletonComponent =
      mode === 'edit' ? ExpenseEditSkeleton : ExpenseCreateSkeleton;
    return (
      <AppLayout>
        <SkeletonComponent />
      </AppLayout>
    );
  }

  if (!group) {
    return (
      <AppLayout>
        <div className="max-w-2xl mx-auto">
          <Alert type="error" message={t('errors.groupNotFound')} />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center mb-8">
          <RouteChangeLink
            href={`/groups/${groupId}`}
            className="mr-4 p-2 rounded-lg hover:bg-[#FAF9F7] dark:hover:bg-gray-800/50 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </RouteChangeLink>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              {t(mode === 'edit' ? 'editExpense' : 'createExpense')}
            </h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {mode === 'edit' ? (
                <RouteChangeLink
                  href={`/groups/${groupId}`}
                  className="text-orange-500 dark:text-orange-400 hover:text-orange-600 dark:hover:text-orange-300 transition-colors font-medium"
                >
                  {group.name}
                </RouteChangeLink>
              ) : (
                <span>
                  {t('labels.addExpenseTo')}{' '}
                  <RouteChangeLink
                    href={`/groups/${groupId}`}
                    className="text-orange-500 dark:text-orange-400 hover:text-orange-600 dark:hover:text-orange-300 transition-colors font-medium"
                  >
                    {group.name}
                  </RouteChangeLink>
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Error Alert; the limit alert below already says the same thing. */}
        {error &&
          !(
            form.saveBlockedByLimit &&
            typeof error !== 'string' &&
            error.key === GROUP_EXPENSE_LIMIT_ERROR_KEYS.reached
          ) && (
            <Alert
              type="error"
              message={
                typeof error === 'string' ? error : t(error.key, error.params)
              }
              data-testid="alert"
              className="mb-6"
            />
          )}

        {expenseLimit && (
          <ExpenseLimitNotice
            limit={expenseLimit}
            mode={mode}
            proAvailable={proFlagOn}
            onMakePro={() => setProDialogOpen(true)}
          />
        )}
        {/* Only the limit alert opens it. */}
        {proFlagOn && form.saveBlockedByLimit && (
          <ProGroupDialogContainer
            isOpen={proDialogOpen}
            onClose={() => setProDialogOpen(false)}
            group={{
              ...group,
              balances: group.members?.map(member => ({
                userId: member.userId._id,
                userName: member.userId.name,
              })),
            }}
          />
        )}

        {/* Form */}
        <FormErrorBoundary>
          {/* The scan machine reads the saved receipt once, so the body
              mounts again once the edit form has hydrated. */}
          <ExpenseFormBody
            key={mode === 'edit' ? (form.hydrationKey ?? 'pending') : 'create'}
            form={form}
            groupId={groupId}
            expenseId={expenseId}
            mode={mode}
          />
        </FormErrorBoundary>
      </div>
    </AppLayout>
  );
}

interface ExpenseFormBodyProps {
  form: ExpenseFormState;
  groupId: string;
  expenseId?: string;
  mode: 'create' | 'edit';
}

/** The form itself: owns the receipt scan and wires it into the fields. */
function ExpenseFormBody({
  form,
  groupId,
  expenseId,
  mode,
}: ExpenseFormBodyProps) {
  const { t, i18n } = useTranslation('expenses');
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [isClosingAdvanced, setIsClosingAdvanced] = useState(false);
  const [viewReceiptUrl, setViewReceiptUrl] = useState<string | null>(null);
  const [itemsDialogOpen, setItemsDialogOpen] = useState(false);
  const advancedOptionsRef = useRef<HTMLDivElement>(null);

  // Handle closing with animation
  const handleCloseAdvanced = () => {
    setIsClosingAdvanced(true);
    setTimeout(() => {
      setShowAdvancedOptions(false);
      setIsClosingAdvanced(false);
    }, 280); // Slightly less than animation duration
  };

  // Scroll to advanced options when expanded
  useEffect(() => {
    if (showAdvancedOptions && advancedOptionsRef.current) {
      // Small delay to allow DOM to update
      setTimeout(() => {
        advancedOptionsRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }, 100);
    }
  }, [showAdvancedOptions]);

  const {
    // Form data
    title,
    setTitle,
    amount,
    setAmount,
    numericAmount,
    setNumericAmount,
    currency,
    // Foreign-currency entry
    groupCurrency,
    enteredCurrency,
    setEnteredCurrency,
    enteredAmount,
    conversion,
    preview,
    setManualRate,
    clearManualRate,
    isOfflineQueue,
    category,
    setCategory,
    handleCategoryChange,
    subcategory,
    setSubcategory,
    setCategoryManuallyChanged,
    categoryPredicted,
    isPredicting,
    onTitleBlur,
    date,
    setDate,
    paidBy,
    setPaidBy,
    splitMethod,
    splits,
    setSplits,
    holdSplitOnMemberChange,
    placeSavedSpot,
    newSpotIds,
    notes,
    setNotes,

    // Repeat
    repeatAvailable,
    repeatCadence,
    setRepeatCadence,
    repeatEndDate,
    setRepeatEndDate,
    repeatPreview,
    repeatPlainOnly,

    // Receipt state
    receiptFile,
    setReceiptFile,
    existingReceiptUrl,
    receiptDeleted,
    setReceiptDeleted,
    setReceiptDraftId,
    isOnline,

    // Receipt scan
    scanSnapshot,
    applyScanFill,
    setScanInFlight,
    setReceiptBusy,

    // Split by item
    items,
    itemsMode,
    itemsAllowed,
    itemsPreview,
    itemsAmount,
    itemsCurrency,
    setAmountFromItems,
    selectItemsMode,
    chooseSplitMethod,
    setLineItems,
    setLineItemPicks,
    setItemsRest,
    toggleItemsParticipant,
    onScanLines,
    pendingItemsReplace,
    confirmReplaceItems,
    keepItems,
    pendingGroupSwitch,
    confirmGroupSwitch,
    cancelGroupSwitch,
    saveAsCustomSplit,

    // Computed values
    group,
    members,

    // Loading states
    isLoading,

    // Form submission
    handleFormSubmit,
    error,
  } = form;
  const user = form.user!;

  // Only surface the "Categorizing…" affordance while a prediction is actually
  // blocking Save — i.e. there's no usable category yet (`!categoryPredicted`)
  // — and it has been running long enough to be worth showing (~150ms). The
  // `!categoryPredicted` gate keeps the label off an already-enabled button
  // when a later edit re-predicts in the background; the synchronous keyword
  // tier resolves in a few ms and the delay hides that.
  const showCategorizing = useDelayedFlag(isPredicting && !categoryPredicted);

  const flagOn = useFeatureFlag('pro');
  const { proNotAvailableSeen, fairUseHit } = useScanSessionFlags();
  const availability = scanAvailability({
    flagOn,
    proNotAvailableSeen,
    fairUseHit,
    isOnline,
    isLocalRow:
      mode === 'edit' && offlineExpenseService.isLocalId(expenseId ?? ''),
    isExampleGroup: groupId === EXAMPLE_GROUP_ID,
    user,
    group: group as { pro?: GroupProDto } | null,
    nowMonth: currentUtcMonth(),
  });
  // Hidden (flag off, ProNotAvailable, example group, queued row) keeps
  // today's dropzone and save path untouched.
  const blockShown = availability.kind !== 'hidden';

  const tagsRef = useRef<ReadonlySet<ScanFieldKey>>(new Set());
  const scan = useReceiptScan({
    groupId,
    groupCurrency,
    locale: i18n.language,
    mode,
    savedReceipt: existingReceiptUrl,
    collapsed: mode === 'edit',
    snapshot: () => ({ ...scanSnapshot(), aiTagged: new Set(tagsRef.current) }),
    onFill: fills => {
      applyScanFill(fills);
      if (fills.date !== undefined) setShowAdvancedOptions(true);
    },
    onLines: (lines, reconciled) => {
      setShowAdvancedOptions(true);
      onScanLines(lines, reconciled);
    },
  });
  const { machine, tags, locked, submitLabel } = scan;
  useEffect(() => {
    tagsRef.current = tags;
  });

  useEffect(() => {
    setScanInFlight(scan.scanInFlight);
  }, [scan.scanInFlight, setScanInFlight]);
  useEffect(() => () => setScanInFlight(false), [setScanInFlight]);

  const receiptBusy =
    scan.state.kind === 'saved'
      ? scan.state.replacing !== null && scan.state.replacing !== undefined
      : scan.state.kind !== 'actions';
  useEffect(() => {
    setReceiptBusy(receiptBusy);
  }, [receiptBusy, setReceiptBusy]);
  useEffect(() => () => setReceiptBusy(false), [setReceiptBusy]);

  useEffect(() => {
    setReceiptDraftId(blockShown ? scan.draftId : null);
  }, [blockShown, scan.draftId, setReceiptDraftId]);

  // Remove on the saved receipt leaves the `saved` state; the save then
  // sends `receipt: null` with a draft, or deletes it without one.
  const savedReceiptRemoved = scan.state.kind !== 'saved';
  useEffect(() => {
    if (blockShown && existingReceiptUrl) {
      setReceiptDeleted(savedReceiptRemoved);
    }
  }, [blockShown, existingReceiptUrl, savedReceiptRemoved, setReceiptDeleted]);

  // The action rows collapse, one way, once the amount or title had a value.
  const [hadValue, setHadValue] = useState(mode === 'edit');
  if (!hadValue && (title !== '' || !!amount)) setHadValue(true);
  const blockState =
    scan.state.kind === 'actions' && hadValue && !scan.state.collapsed
      ? { ...scan.state, collapsed: true }
      : scan.state;

  const shownDraft =
    scan.state.kind === 'saved' ? scan.state.replacing : scan.state;
  const checkCurrency =
    shownDraft?.kind === 'read' && !!shownDraft.merge?.checkCurrency;
  const receiptUrl =
    shownDraft && 'localUri' in shownDraft
      ? shownDraft.localUri
      : scan.state.kind === 'saved'
        ? scan.state.url
        : !blockShown && !receiptDeleted
          ? existingReceiptUrl
          : null;

  // The caller first, as "You"; everyone else in group order.
  const itemsPeople = splits
    .filter(split => split.included)
    .map(split => ({
      userId: split.userId,
      name: split.userId === user.id ? t('labels.you') : split.userName,
      profileImage: split.profileImage,
    }))
    .sort(
      (a, b) => Number(b.userId === user.id) - Number(a.userId === user.id)
    );
  const itemsReadOnly = itemsMode && !itemsAllowed;
  const queuedItemsRefused = useOfflineStore(
    state =>
      mode === 'edit' &&
      !!expenseId &&
      offlineExpenseService.isLocalId(expenseId) &&
      state.pendingExpenses.find(e => e.localId === expenseId)?.lastError ===
        SYNC_LINE_ITEMS_NOT_ALLOWED_ERROR
  );

  // An edit makes the value the person's: the tag goes, and the machine
  // stops treating the field as scan-filled even after Remove took the tag.
  const clearTag = (field: ScanFieldKey) => machine.clearTag(field);
  const onTitleChange = (value: string) => {
    if (value !== title) clearTag('title');
    setTitle(value);
  };
  const onNumericAmountChange = (value: number) => {
    if (value !== enteredAmount) clearTag('amount');
    setNumericAmount(value);
  };
  const onCurrencyChange = (value: string) => {
    if (value !== enteredCurrency) clearTag('amount');
    setEnteredCurrency(value);
  };
  const onDateChange = (value: string) => {
    if (value !== date) clearTag('date');
    setDate(value);
  };
  const onCategoryChange = (value: string) => {
    clearTag('category');
    handleCategoryChange(value);
  };
  const onCategoryDirect = (value: string) => {
    clearTag('category');
    setCategory(value);
  };
  const onSubcategoryChange = (value: string | undefined) => {
    clearTag('category');
    setSubcategory(value);
  };

  const useRowContext: DescribeUseRowContext = {
    formatCurrency: (value, code) => formatCurrency(value, code, i18n.language),
    formatDate: value => formatDate(value, i18n.language),
    categoryLabel: main => t(`categories.${main}`),
    subcategoryLabel: (main, sub) =>
      ExpenseBasicInfoService.formatSubcategoryName(sub, main, t),
    fieldLabel: field => t(`scan.field.${field}`),
    separator: ' · ',
    fallbackCurrency: enteredCurrency,
  };
  const describeScanUseRow = (row: UseRow) => {
    const mine = describeUseRow(row, 'mine', useRowContext);
    return {
      label: mine.label,
      mine: mine.value,
      theirs: describeUseRow(row, 'theirs', useRowContext).value,
    };
  };

  // The lock covers every control; an upload only holds the submit.
  const controlsBusy = isLoading || locked;

  const spot = useSaveSpotFromForm({
    groupId,
    isOnline,
    members,
    holdSplitOnMemberChange,
    placeSavedSpot,
  });
  const saveSpotField = (role: SavedSpotRole) =>
    spot.offered ? (
      <SaveSpotField
        onSave={name => spot.createAndPlace(name, role)}
        available={spot.available}
        disabled={controlsBusy}
        testId={`${role}-save-spot`}
      />
    ) : undefined;

  const submitBlocked =
    form.saveBlockedByLimit ||
    !title ||
    !numericAmount ||
    !categoryPredicted ||
    isLoading ||
    submitLabel !== null;
  const scanSubmitText =
    submitLabel === 'reading'
      ? t('scan.submitReading')
      : submitLabel === 'uploading'
        ? t('scan.uploading')
        : null;

  // `ConversionPreview` deals in a single nullable setter; the hook splits
  // "set an override" and "clear it" into two calls.
  const handleManualRateChange = (rate: number | null) => {
    if (rate === null) {
      clearManualRate();
    } else {
      setManualRate(rate);
    }
  };

  return (
    <>
      <form
        onSubmit={e => handleFormSubmit(e, {})}
        className="space-y-6"
        data-testid="expense-form"
      >
        {queuedItemsRefused && itemsMode && (
          <div
            data-testid="queued-items-refused"
            className="flex flex-wrap items-center gap-3 rounded-lg border border-warning-200 bg-warning-50 px-4 py-3 dark:border-warning-800 dark:bg-warning-900/20"
          >
            <p className="min-w-[45%] flex-1 text-sm text-warning-700 dark:text-warning-300">
              {t('lineItems.syncNotAllowed')}
            </p>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={saveAsCustomSplit}
              disabled={isLoading}
              className="ml-auto min-h-11"
            >
              {t('lineItems.saveAsCustom')}
            </Button>
          </div>
        )}

        {blockShown && (
          <ReceiptBlock
            scan={{ state: blockState, machine }}
            availability={availability}
            groupId={groupId}
            describeUseRow={describeScanUseRow}
            onView={setViewReceiptUrl}
            itemsAllowed={itemsAllowed}
          />
        )}

        {/* Layer 1: Quick Entry - Always visible */}
        <ExpenseBasicInfo
          title={title}
          setTitle={onTitleChange}
          onTitleBlur={onTitleBlur}
          groupCurrency={groupCurrency}
          enteredCurrency={enteredCurrency}
          onCurrencyChange={onCurrencyChange}
          amount={amount}
          setAmount={setAmount}
          numericAmount={numericAmount}
          setNumericAmount={onNumericAmountChange}
          date={date}
          setDate={onDateChange}
          isLoading={controlsBusy}
          enteredAmount={enteredAmount}
          conversion={conversion}
          preview={preview}
          onManualRateChange={handleManualRateChange}
          isOfflineQueue={isOfflineQueue}
          category={category}
          setCategory={onCategoryDirect}
          handleCategoryChange={onCategoryChange}
          subcategory={subcategory}
          setSubcategory={onSubcategoryChange}
          setCategoryManuallyChanged={setCategoryManuallyChanged}
          showCategory={categoryPredicted}
          aiTags={blockShown ? tags : undefined}
          checkCurrency={blockShown && checkCurrency}
        />

        {/* Receipt Photo - Visible once category is predicted */}
        {categoryPredicted && (
          <>
            {!blockShown &&
              (isOnline ? (
                <ExpenseReceiptUpload
                  file={receiptFile}
                  existingUrl={!receiptDeleted ? existingReceiptUrl : null}
                  onFileSelected={setReceiptFile}
                  onFileRemoved={() => {
                    setReceiptFile(null);
                    setReceiptDeleted(true);
                  }}
                  disabled={isLoading}
                  data-testid="expense-receipt-upload"
                />
              ) : (
                <ExpenseReceiptOfflineNotice data-testid="expense-receipt-offline-notice" />
              ))}

            {/* Split recap (green card) */}
            <SplitRecap
              splits={splits}
              totalAmount={numericAmount}
              currency={currency}
              splitMethod={splitMethod}
              paidBy={paidBy}
              currentUserId={user.id}
              onExpand={() => setShowAdvancedOptions(!showAdvancedOptions)}
              isLoading={controlsBusy}
              isExpanded={showAdvancedOptions}
              items={
                items &&
                (itemsMode || (itemsAllowed && items.lineItems.length > 0))
                  ? {
                      active: itemsMode,
                      count: items.lineItems.length,
                      fromScan: items.fromScan,
                      unreconciled: items.scanUnreconciled,
                      shares: itemsPreview?.entryShares ?? [],
                      currency: itemsCurrency,
                    }
                  : undefined
              }
              onSplitByItem={() => {
                // Picks made with items mode off would not be sent.
                selectItemsMode();
                setItemsDialogOpen(true);
              }}
            />

            {/* Progressive Disclosure Toggle */}
            <ProgressiveDisclosureToggle
              isExpanded={showAdvancedOptions}
              onToggle={() => {
                if (showAdvancedOptions) {
                  handleCloseAdvanced();
                } else {
                  setShowAdvancedOptions(true);
                }
              }}
              disabled={controlsBusy || isClosingAdvanced}
            />

            {/* Layer 2: Advanced Options - Animated expand/collapse */}
            {showAdvancedOptions && (
              <div
                className="space-y-6"
                ref={advancedOptionsRef}
                style={{
                  animation: isClosingAdvanced
                    ? 'slideUp 0.3s ease-out forwards'
                    : 'slideDown 0.3s ease-out',
                }}
              >
                <style>{`
                  @keyframes slideDown {
                    from {
                      opacity: 0;
                      transform: translateY(-10px);
                    }
                    to {
                      opacity: 1;
                      transform: translateY(0);
                    }
                  }
                  @keyframes slideUp {
                    from {
                      opacity: 1;
                      transform: translateY(0);
                    }
                    to {
                      opacity: 0;
                      transform: translateY(-10px);
                    }
                  }
                `}</style>
                {/* Action buttons at the top of advanced options */}
                <div className="flex justify-end gap-3">
                  <Button
                    variant="outline"
                    size="md"
                    type="button"
                    onClick={handleCloseAdvanced}
                    disabled={controlsBusy || isClosingAdvanced}
                    className="shrink-0"
                  >
                    {t('common:actions.close')}
                  </Button>
                  <Button
                    variant="primary"
                    size="md"
                    type="button"
                    onClick={() =>
                      handleFormSubmit(
                        new Event(
                          'submit'
                        ) as unknown as React.FormEvent<HTMLFormElement>,
                        {}
                      )
                    }
                    disabled={submitBlocked}
                    leftIcon={
                      showCategorizing || submitLabel === 'reading' ? (
                        <SparklesIcon className="h-4 w-4 animate-pulse motion-reduce:animate-none" />
                      ) : (
                        <SaveIcon className="h-4 w-4" />
                      )
                    }
                    className="shrink-0"
                  >
                    {scanSubmitText ??
                      (showCategorizing
                        ? t('categorizing')
                        : isLoading
                          ? t('common:actions.saving')
                          : t('common:actions.save'))}
                  </Button>
                </div>

                <UnifiedExpenseSplit
                  splits={splits}
                  setSplits={setSplits}
                  splitMethod={splitMethod}
                  setSplitMethod={chooseSplitMethod}
                  totalAmount={numericAmount}
                  currency={currency}
                  currentUserId={user.id}
                  newSpotIds={newSpotIds}
                  isLoading={controlsBusy}
                  itemsAllowed={itemsAllowed}
                  itemsMode={itemsMode}
                  itemCount={items?.lineItems.length ?? 0}
                  onSelectItems={() => {
                    selectItemsMode();
                    setItemsDialogOpen(true);
                  }}
                  itemsContent={
                    items ? (
                      <SplitByItemCard
                        members={splits.map(split => ({
                          userId: split.userId,
                          name:
                            split.userId === user.id
                              ? t('labels.you')
                              : split.userName,
                          profileImage: split.profileImage,
                          included: split.included,
                        }))}
                        onToggleParticipant={toggleItemsParticipant}
                        itemCount={items.lineItems.length}
                        unpickedUnits={unpickedUnits(items.lineItems)}
                        fromScan={items.fromScan}
                        scanUnreconciled={items.scanUnreconciled}
                        shares={itemsPreview?.entryShares ?? []}
                        currency={itemsCurrency}
                        onOpen={() => setItemsDialogOpen(true)}
                        disabled={controlsBusy}
                        readOnly={itemsReadOnly}
                      />
                    ) : null
                  }
                  saveSpot={saveSpotField('sharer')}
                />

                <ExpensePaymentDetails
                  members={members}
                  payers={paidBy}
                  setPayers={setPaidBy}
                  totalAmount={numericAmount}
                  currency={currency}
                  currentUserId={user.id}
                  isLoading={controlsBusy}
                  saveSpot={saveSpotField('payer')}
                  error={
                    error
                      ? typeof error === 'string'
                        ? error
                        : t(error.key, error.params)
                      : undefined
                  }
                />

                <ExpenseDate
                  date={date}
                  setDate={onDateChange}
                  isLoading={controlsBusy}
                  aiTagged={blockShown && tags.has('date')}
                />

                {repeatAvailable && (
                  <ExpenseRepeat
                    cadence={repeatCadence}
                    onCadenceChange={setRepeatCadence}
                    endDate={repeatEndDate}
                    onEndDateChange={setRepeatEndDate}
                    preview={repeatPreview}
                    disabled={controlsBusy}
                  />
                )}
                {!repeatAvailable && repeatPlainOnly && (
                  <p
                    data-testid="expense-repeat-plain-only"
                    className="text-sm text-gray-600 dark:text-gray-400"
                  >
                    {t('repeat.plainOnly')}
                  </p>
                )}

                <ExpenseAdditionalDetails
                  notes={notes}
                  setNotes={setNotes}
                  totalAmount={numericAmount}
                  isLoading={controlsBusy}
                />
              </div>
            )}
          </>
        )}

        {/* Form Actions - No box, page background */}
        <div className="flex justify-end space-x-3 pt-6">
          <BackButton
            variant="outline"
            fallbackUrl={`/groups/${groupId}`}
            text={t('common:actions.cancel')}
            data-testid="expense-cancel-button"
          />
          <FormButton
            type="submit"
            disabled={submitBlocked}
            loading={isLoading}
            loadingText={t(
              mode === 'edit'
                ? 'common:actions.saving'
                : 'common:actions.creating'
            )}
            icon={showCategorizing || scanSubmitText ? undefined : SaveIcon}
            data-testid="expense-submit-button"
          >
            {scanSubmitText ? (
              <span className="inline-flex items-center gap-2">
                {submitLabel === 'reading' ? (
                  <SparklesIcon className="h-4 w-4 animate-pulse motion-reduce:animate-none" />
                ) : null}
                {scanSubmitText}
              </span>
            ) : showCategorizing ? (
              <span className="inline-flex items-center gap-2">
                <SparklesIcon className="h-4 w-4 animate-pulse motion-reduce:animate-none" />
                {t('categorizing')}
              </span>
            ) : (
              t(mode === 'edit' ? 'common:actions.save' : 'createExpense')
            )}
          </FormButton>
        </div>
      </form>
      {items ? (
        <SplitByItemDialog
          isOpen={itemsDialogOpen}
          onClose={() => setItemsDialogOpen(false)}
          people={itemsPeople}
          currentUserId={user.id}
          lineItems={items.lineItems}
          rest={items.rest}
          scanReconciled={items.scanReconciled}
          scanUnreconciled={items.scanUnreconciled}
          preview={itemsPreview}
          amount={itemsAmount}
          currency={itemsCurrency}
          onItemsChange={setLineItems}
          onPicksChange={setLineItemPicks}
          onRestChange={setItemsRest}
          onSetTotal={value => {
            clearTag('amount');
            setAmountFromItems(value);
          }}
          onViewReceipt={
            receiptUrl ? () => setViewReceiptUrl(receiptUrl) : undefined
          }
          readOnly={itemsReadOnly}
        />
      ) : null}
      <ConfirmationDialog
        isOpen={pendingItemsReplace}
        onClose={keepItems}
        onConfirm={confirmReplaceItems}
        title={t('scan.replaceItemsTitle')}
        description={t('scan.replaceItemsBody')}
        confirmText={t('scan.replaceItems')}
        cancelText={t('scan.keepItems')}
        variant="warning"
      />
      <ConfirmationDialog
        isOpen={pendingGroupSwitch !== null}
        onClose={cancelGroupSwitch}
        onConfirm={confirmGroupSwitch}
        title={
          pendingGroupSwitch
            ? t(`lineItems.${switchConfirmKey(pendingGroupSwitch)}`)
            : ''
        }
        description={t('lineItems.switchBody')}
        confirmText={t('lineItems.switch')}
        cancelText={t('common:actions.cancel')}
        variant="warning"
      />
      {viewReceiptUrl && isPdfReceiptUrl(viewReceiptUrl) ? (
        <ExpenseReceiptModal
          isOpen
          onClose={() => setViewReceiptUrl(null)}
          imageUrl={viewReceiptUrl}
          kind="pdf"
        />
      ) : viewReceiptUrl ? (
        <ImageViewerModal
          isOpen
          onClose={() => setViewReceiptUrl(null)}
          imageUrl={viewReceiptUrl}
          title={t('scan.viewReceipt')}
        />
      ) : null}
    </>
  );
}

export default withAuth(ExpenseForm, { allowOffline: true });
